import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Avatar,
  Paper,
  Chip
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Check as CheckIcon,
  DoneAll as DoneAllIcon
} from '@mui/icons-material';
// Fonctions simples pour formater les dates
const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

const formatDateSeparator = (timestamp) => {
  const date = new Date(timestamp);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  if (date.toDateString() === today.toDateString()) {
    return 'Aujourd\'hui';
  } else if (date.toDateString() === yesterday.toDateString()) {
    return 'Hier';
  } else {
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  }
};

const ChatWindow = ({ conversation, currentUser }) => {
  const messagesEndRef = useRef(null);
  const { messages, typingUsers } = useSelector(state => state.messages);
  
  const conversationMessages = messages[conversation.id] || [];

  // Scroll automatique vers le dernier message
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  const getParticipantById = (id) => {
    return conversation.participants.find(p => p.id === id);
  };



  const shouldShowDateSeparator = (currentMessage, previousMessage) => {
    if (!previousMessage) return true;
    
    const currentDate = new Date(currentMessage.timestamp);
    const previousDate = new Date(previousMessage.timestamp);
    
    return currentDate.toDateString() !== previousDate.toDateString();
  };

  const getOtherParticipant = () => {
    return conversation.participants.find(p => p.id !== currentUser.id);
  };

  const renderTypingIndicator = () => {
    const otherParticipant = getOtherParticipant();
    const isOtherUserTyping = typingUsers.includes(otherParticipant?.id);
    
    if (!isOtherUserTyping) return null;

    return (
      <Box className="typing-indicator-container">
        <Avatar
          src={otherParticipant?.avatar}
          alt={otherParticipant?.name}
          className="typing-avatar"
        >
          {otherParticipant?.type === 'organization' ? (
            <BusinessIcon />
          ) : (
            <PersonIcon />
          )}
        </Avatar>
        
        <Paper className="typing-bubble" elevation={1}>
          <Box className="typing-dots">
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
          </Box>
        </Paper>
      </Box>
    );
  };

  return (
    <Box className="chat-window">
      {/* En-tête de conversation */}
      <Box className="chat-header">
        <Avatar
          src={getOtherParticipant()?.avatar}
          alt={getOtherParticipant()?.name}
          className="chat-header-avatar"
        >
          {getOtherParticipant()?.type === 'organization' ? (
            <BusinessIcon />
          ) : (
            <PersonIcon />
          )}
        </Avatar>
        
        <Box className="chat-header-info">
          <Typography variant="h6" className="chat-header-name">
            {getOtherParticipant()?.name}
          </Typography>
          
          <Box className="chat-header-status">
            <Chip
              label={getOtherParticipant()?.type === 'organization' ? 'Organisation' : 'Professionnel'}
              size="small"
              className={`header-type-chip ${
                getOtherParticipant()?.type === 'organization' 
                  ? 'organization-chip' 
                  : 'professional-chip'
              }`}
            />
            
            <Typography 
              variant="caption" 
              className={`online-status-text ${
                getOtherParticipant()?.isOnline ? 'online' : 'offline'
              }`}
            >
              {getOtherParticipant()?.isOnline ? 'En ligne' : 'Hors ligne'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Zone des messages */}
      <Box className="messages-container">
        {conversationMessages.length === 0 ? (
          <Box className="no-messages">
            <Typography variant="body2" color="textSecondary">
              Aucun message dans cette conversation
            </Typography>
          </Box>
        ) : (
          conversationMessages.map((message, index) => {
            const sender = getParticipantById(message.senderId);
            const isCurrentUser = message.senderId === currentUser.id;
            const previousMessage = index > 0 ? conversationMessages[index - 1] : null;
            const showDateSeparator = shouldShowDateSeparator(message, previousMessage);

            return (
              <React.Fragment key={message.id}>
                {/* Séparateur de date */}
                {showDateSeparator && (
                  <Box className="date-separator">
                    <Typography variant="caption" className="date-separator-text">
                      {formatDateSeparator(message.timestamp)}
                    </Typography>
                  </Box>
                )}

                {/* Message */}
                <Box 
                  className={`message-wrapper ${
                    isCurrentUser ? 'message-sent' : 'message-received'
                  }`}
                >
                  {!isCurrentUser && (
                    <Avatar
                      src={sender?.avatar}
                      alt={sender?.name}
                      className="message-avatar"
                    >
                      {sender?.type === 'organization' ? (
                        <BusinessIcon />
                      ) : (
                        <PersonIcon />
                      )}
                    </Avatar>
                  )}
                  
                  <Paper 
                    className={`message-bubble ${
                      isCurrentUser ? 'message-bubble-sent' : 'message-bubble-received'
                    } ${
                      sender?.type === 'organization' ? 'organization-message' : 'professional-message'
                    }`}
                    elevation={1}
                  >
                    {!isCurrentUser && (
                      <Typography variant="caption" className="message-sender-name">
                        {sender?.name}
                      </Typography>
                    )}
                    
                    <Typography variant="body1" className="message-content">
                      {message.content}
                    </Typography>
                    
                    <Box className="message-footer">
                      <Typography variant="caption" className="message-time">
                        {formatMessageTime(message.timestamp)}
                      </Typography>
                      
                      {isCurrentUser && (
                        <Box className="message-status">
                          {message.read ? (
                            <DoneAllIcon className="message-read-icon" />
                          ) : (
                            <CheckIcon className="message-sent-icon" />
                          )}
                        </Box>
                      )}
                    </Box>
                  </Paper>
                </Box>
              </React.Fragment>
            );
          })
        )}
        
        {/* Indicateur de frappe */}
        {renderTypingIndicator()}
        
        {/* Référence pour le scroll automatique */}
        <div ref={messagesEndRef} />
      </Box>
    </Box>
  );
};

export default ChatWindow;